import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAN from 'src/assets/img/flags/Canada-sm.svg';
import US from 'src/assets/img/flags/US-sm.svg';
import CommonModal from 'src/components/CommonModal';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { CURRENCY } from 'src/constants/common';

interface IBookOrderBillingSelection {
    division: any;
    billingAccount: any;
}

interface IProps {
    // Divisions to choose from, each carrying its own customerBillingAccount list. Resolved by the
    // caller and passed in, so the options shown are exactly the ones the caller counted when it
    // decided this modal was needed.
    divisions: any[];
    // The division already fixed by the quote, when it has one. Preselected and, when the quote
    // also fixes it, the division field is hidden entirely.
    fixedDivisionId?: number | string | null;
    // Which fields the customer still has to answer. A field the quote already answers is not shown.
    showDivision: boolean;
    showBillingAccount: boolean;
    handleClose: () => void;
    // Receives the chosen records (not just ids) — the caller needs displayName, currency and
    // quickBookId to show them on the booking review screen.
    onConfirm: (selection: IBookOrderBillingSelection) => void;
}

// Currency flag shown on the closed value and in the option list, matching the New Quote modal.
const getCurrencyFlagIcon = (currency: string | null) => {
    if (currency === CURRENCY.CAD) return <img src={CAN} alt="Canada" className="w-4 h-4 object-cover rounded flex-none" />;
    if (currency === CURRENCY.USD) return <img src={US} alt="United States" className="w-4 h-4 object-cover rounded flex-none" />;

    return null;
};

/**
 * BookOrderBillingAccountModal — asks for the division and/or billing account an order will be
 * booked under, before the booking stepper opens.
 *
 * A quote can reach the detail page missing either one, and the order cannot be invoiced without
 * both, so whichever is missing is collected here. Only the missing field is shown: re-asking for
 * something the quote already fixes would invite an answer that contradicts the quote being booked.
 * The account list always follows the division in view, so the pair can never disagree.
 */
const BookOrderBillingAccountModal = ({ divisions, fixedDivisionId, showDivision, showBillingAccount, handleClose, onConfirm }: IProps) => {
    const { t } = useTranslation();

    const divisionOptions = useMemo(
        () =>
            divisions.map((division: any) => ({
                label: division.displayName,
                value: division.id,
            })),
        [divisions]
    );

    const [selectedDivision, setSelectedDivision] = useState<any>(() => {
        if (fixedDivisionId) {
            return divisionOptions.find((option) => String(option.value) === String(fixedDivisionId)) ?? null;
        }

        // Only one to choose from — preselect it rather than making the customer open the dropdown.
        return divisionOptions.length === 1 ? divisionOptions[0] : null;
    });

    const billingAccounts = useMemo(() => {
        const division = divisions.find((item: any) => String(item.id) === String(selectedDivision?.value));

        return division?.customerBillingAccount ?? [];
    }, [divisions, selectedDivision]);

    const billingAccountOptions = useMemo(
        () =>
            billingAccounts.map((account: any) => {
                // Upper-cased here because the stored column is lower-case on virtually every row.
                const currency = account.currency?.toUpperCase() ?? null;

                return {
                    label: `${currency} Billing Account${account.quickBookId ? ` ${account.quickBookId}` : ''}`,
                    value: account.id,
                    icon: getCurrencyFlagIcon(currency),
                };
            }),
        [billingAccounts]
    );

    const [selectedAccountOption, setSelectedAccountOption] = useState<any>(null);
    const [submitted, setSubmitted] = useState(false);

    // Changing the division invalidates the account under it. Reselect the only option when the new
    // division has exactly one, otherwise clear so the customer cannot submit a stale pairing.
    useEffect(() => {
        setSelectedAccountOption(billingAccountOptions.length === 1 ? billingAccountOptions[0] : null);
    }, [billingAccountOptions]);

    const handleSave = () => {
        setSubmitted(true);

        const division = divisions.find((item: any) => String(item.id) === String(selectedDivision?.value)) ?? null;
        const billingAccount = billingAccounts.find((item: any) => String(item.id) === String(selectedAccountOption?.value)) ?? null;

        if (!division || (showBillingAccount && !billingAccount)) {
            return;
        }

        onConfirm({ division, billingAccount });
        handleClose();
    };

    return (
        <CommonModal
            title={t('MyQuote.Book_Order_Billing_Account_Title')}
            handleClose={handleClose}
            size="max-w-[480px]"
            primaryBtnText={t('Common.Continue')}
            primaryBtnOnClick={handleSave}
            secondaryBtnText={t('Common.Cancel')}
            secondaryBtnOnClick={handleClose}
        >
            <div className="p-5 flex flex-col gap-3">
                <p className="text-textSecondary text-xs leading-[18px] font-normal">{t('MyQuote.Book_Order_Billing_Account_Description')}</p>

                {showDivision && (
                    <SelectBox
                        label={t('MyQuote.Select_Division')}
                        labelClassName="form_label !mb-1.5"
                        name="bookOrderDivision"
                        placeholder={t('MyQuote.Select_Division_Placeholder')}
                        options={divisionOptions}
                        value={selectedDivision}
                        onChangeFunc={(option: any) => setSelectedDivision(option)}
                        isError={submitted && !selectedDivision}
                        required
                    />
                )}

                {showBillingAccount && (
                    <SelectBox
                        label={t('MyQuote.Select_Account')}
                        labelClassName="form_label !mb-1.5"
                        name="bookOrderBillingAccount"
                        placeholder={t('MyQuote.Select_Billing_Account_Placeholder')}
                        options={billingAccountOptions}
                        value={selectedAccountOption}
                        isDisabled={!selectedDivision}
                        onChangeFunc={(option: any) => setSelectedAccountOption(option)}
                        isError={submitted && !selectedAccountOption}
                        required
                    />
                )}
            </div>
        </CommonModal>
    );
};

export default BookOrderBillingAccountModal;
